import Link from "next/link"
import Image from "next/image"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ArrowRight, MapPin } from "lucide-react"

/** When no URL is set in admin, show a neutral placeholder (project convention). */
const PLACEHOLDER_IMAGE = "https://placekeanu.com/500/500"

export type ScholarshipCardOffer = {
  /** Use in URLs — always safe (cuid). */
  id: string
  offerId: string
  university: string
  location: string | null
  teachingLanguage: string
  intake: string
  imageUrl: string | null
  majors: string[]
}

interface ScholarshipCardProps {
  offer: ScholarshipCardOffer
}

export const ScholarshipCard = ({ offer }: ScholarshipCardProps) => {
  const href = `/scholarships/${offer.id}`
  const imgSrc = offer.imageUrl?.trim() || PLACEHOLDER_IMAGE

  return (
    <Card className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border shadow transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
      <Link
        href={href}
        className="relative block aspect-[16/10] w-full overflow-hidden bg-muted"
        aria-label={`${offer.university} — дэлгэрэнгүй`}
      >
        <Image
          src={imgSrc}
          alt={offer.university}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
      </Link>
      <CardHeader className="space-y-2 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardTitle className="text-lg leading-snug">{offer.university}</CardTitle>
          <Badge variant="outline" className="shrink-0 font-mono text-xs">
            {offer.offerId}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
          {offer.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              {offer.location}
            </span>
          )}
          <span>{offer.teachingLanguage}</span>
          <span>·</span>
          <span>{offer.intake}</span>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 pt-0">
        {offer.majors.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {offer.majors.slice(0, 4).map((m) => (
              <Badge key={m} variant="secondary" className="text-xs font-normal">
                {m}
              </Badge>
            ))}
            {offer.majors.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{offer.majors.length - 4}
              </Badge>
            )}
          </div>
        )}
    
      </CardContent>
    </Card>
  )
}
